import { action, computed, observable } from 'mobx'
import { Api, FilterRange, initialFilterRange, SortDirection } from '../../utilities'
import { RootStore } from 'store/RootStore'
import { GENERAL_STORE } from 'store/GeneralStore'
import {
  Page, Product, ProductDTO, ProductsTableEntryDTOInterface,
  ProductTableEntryDTO, Shipping
} from 'models'
import {
  initialProductsSearchProps, ProductTablePaginationProps,
  ProductsSearchProps, ProductsTableSortProps,
  initialProductTablePaginationProps
} from './ProductStore.types'
import { PaginationButtonsProps } from 'components/shared'
import { computedFn } from 'mobx-utils'
import { OnSelectCallbackParam } from 'react-daterange-picker'
import moment from 'moment'
import { PRODUCT_SKU_STORE } from 'store/ProductSkuStore'

export const PRODUCT_STORE = 'PRODUCT_STORE'

export class ProductStore {
  rootStore: RootStore
  timeoutId: NodeJS.Timeout

  constructor (rootStore: RootStore) {
    this.rootStore = rootStore
  }

  @observable productsList: ProductTableEntryDTO[] = []
  @observable productToView: Product = {} as Product
  @observable productHasChangesToSave: boolean = false
  @observable brandShippings: Shipping[] = []

  @observable searchProps: ProductsSearchProps = initialProductsSearchProps

  @observable pagination: ProductTablePaginationProps = initialProductTablePaginationProps

  @observable skuCountRange: FilterRange = initialFilterRange

  @action.bound setSkuCountRange (value: FilterRange) {
    this.skuCountRange = value
  }

  @action.bound setProductToView (product: Product = {} as Product) {
    this.productToView = product
  }

  @action.bound clearProductToView () {
    this.productToView = {} as Product;
  }

  @action.bound setProductsList (list: ProductTableEntryDTO[] = []) {
    this.productsList = list
  }

  @action.bound setPaginationProps (props: Partial<ProductTablePaginationProps>) {
    this.pagination = { ...this.pagination, ...props }
  }

  @action.bound setSearchProps (props: Partial<ProductsSearchProps>) {
    this.searchProps = {
      ...this.searchProps,
      ...props
    }
  }

  @action.bound setBrandShippings (shippings: Shipping[]) {
    this.brandShippings = shippings
  }

  @action.bound setHasChangesToSave (value: boolean) {
    this.productHasChangesToSave = value
  }

  setProductProps = (props: Partial<ProductDTO>) => {
    this.setProductToView(new Product({
      ...this.productToView.asDTO,
      ...props
    }))
    this.setHasChangesToSave(true)
  }

  createProduct = (productDetails: Product) => {
    const {
      setLoadingOn, setLoadingOff, push, handleApiError
    } = this.rootStore[GENERAL_STORE]

    setLoadingOn()

    Api
      .post('/product/create', productDetails)
      .then(() => {
        setLoadingOff()
        push('/products')
      })
      .catch(handleApiError)
  }

  editProduct = (product: Product = this.productToView) => {
    const {
      setLoadingOn, setLoadingOff, showSuccess, handleApiError
    } = this.rootStore[GENERAL_STORE]

    setLoadingOn()

    Api
      .post('/product/update', product.asDTO)
      .then((data: ProductDTO) => {
        this.setProductToView(new Product(data))
        this.setHasChangesToSave(false)
        setLoadingOff()
        showSuccess('Details have been saved.')
      })
      .catch(handleApiError)
  }

  getProducts = (loadContents = false, loadProjects = false, loadAgreements = false) => {
    const {
      setLoadingOn, setLoadingOff, handleApiError
    } = this.rootStore[GENERAL_STORE]

    setLoadingOn()

    Api
      .get(`/product/all?loadContents=${loadContents
        }&loadProjects=${loadProjects}&loadAgreements=${loadAgreements}`)
      .then((res: Product[]) => {
        this.setProductsList(res as any) // TODO: fix res type
        setLoadingOff()
      })
      .catch(handleApiError)
  }

  getSearchedProducts = (resetPageNumber: boolean = false) => {
    const {
      setLoadingOn, setLoadingOff, handleApiError
    } = this.rootStore[GENERAL_STORE]

    setLoadingOn()

    let minSKUs = this.skuCountRange.min
    let maxSKUs = this.skuCountRange.max

    Api
      .post(
        `/product/all?page=${resetPageNumber ? 0 : this.pagination.page
        }&size=${this.pagination.size
        }&sort=${this.pagination.sortProp},${this.pagination.sortDirection}`,
        this.searchProps
      )
      .then((data: Page<ProductsTableEntryDTOInterface>) => {
        this.setProductsList(data.content.map(p => {
          if (this.skuCountRange.min === null && this.skuCountRange.max === null) {
            if (minSKUs === null) {
              minSKUs = p.skuCount
            } else {
              minSKUs = Math.min(minSKUs, p.skuCount)
            }
            if (maxSKUs === null) {
              maxSKUs = p.skuCount
            } else {
              maxSKUs = Math.max(maxSKUs, p.skuCount)
            }
          }

          return new ProductTableEntryDTO(p)
        }))
        this.setSkuCountRange({
          min: minSKUs,
          max: maxSKUs
        })
        this.setPaginationProps({
          page: data.number,
          size: data.size,
          totalElements: data.totalElements,
          totalPages: data.totalPages
        })
        setLoadingOff()
      })
      .catch(handleApiError)
  }

  loadProductDetails = (id, loadContents = false, loadProjects = false) => {
    const {
      setLoadingOn, setLoadingOff, handleApiError
    } = this.rootStore[GENERAL_STORE]

    setLoadingOn()

    Api
      .get(`/product/${id}/view?loadContents=${loadContents}&loadProjects=${loadProjects}`)
      .then((_: Product) => {
        const cloneProducts = [...this.productsList]
        // TODO: to fix after model changed
        // const index = cloneProducts.findIndex(p => p.id === res.id)
        // cloneProducts[index] = res
        this.setProductsList(cloneProducts)
        setLoadingOff()
      })
      .catch(handleApiError)
  }

  getProductById = async (id, loadContents = false, loadProjects = false) => {
    const {
      setLoadingOn, setLoadingOff, handleApiError
    } = this.rootStore[GENERAL_STORE]

    setLoadingOn()

    let product: ProductDTO = null
    await Api
      .get(`/product/${id}/view?loadContents=${loadContents}&loadProjects=${loadProjects}`)
      .then((res: ProductDTO) => {
        // console.log('productToView:', res);
        this.setProductToView(new Product(res))
        setLoadingOff()
        product = res
      })
      .catch(handleApiError)

    return product
  }

  onPageSelect = (pageNumber: number) => {
    this.setPaginationProps({ page: pageNumber })
    this.getSearchedProducts()
  }

  setSortDirection = (sortProp: ProductsTableSortProps, sortDirection: SortDirection) => {
    this.setPaginationProps({
      sortProp,
      sortDirection
    })
    this.getSearchedProducts(true)
  }

  onSearchPropsChange = (newProps: Partial<ProductsSearchProps>) => {
    this.setSearchProps(newProps)

    if (this.timeoutId) {
      clearTimeout(this.timeoutId)
    }

    const needTimeoutProps: Array<keyof ProductsSearchProps> = [
      'productName', 'minSKUs', 'maxSKUs', 'brandName', 'projectName',
      'agreementName', 'tags'
    ]

    if (needTimeoutProps.includes(Object.keys(newProps)[0] as any)) {
      this.timeoutId = setTimeout(() => this.getSearchedProducts(true), 800)
    } else {
      this.getSearchedProducts(true)
    }
  }

  onSkuCountChange = ([minSKUs, maxSKUs]: number[]) => {
    this.onSearchPropsChange({ minSKUs, maxSKUs })
  }

  onCreatedDateFilterChange = (period: OnSelectCallbackParam) => {
    this.onSearchPropsChange({
      startDate: period ? period.start.format('YYYY-MM-DD') : null,
      endDate: period ? period.end.format('YYYY-MM-DD') : null
    })
  }

  getBrandShippings = (brandId: number | string) => {
    const {
      setLoadingOn, setLoadingOff, handleApiError
    } = this.rootStore[GENERAL_STORE]

    setLoadingOn()

    Api
      .get(`/brand/${brandId}/shippings`)
      .then((shippings: Shipping[]) => {
        this.setBrandShippings(shippings)
        setLoadingOff()
      })
      .catch(handleApiError)
  }

  switchShippingEnable = (shippingId: number) => {
    let shippings = [...(this.productToView.shippings || [])]

    if (this.productShippingIds.has(shippingId)) {
      shippings = shippings.filter(shipping => shipping.id !== shippingId)
    } else {
      shippings.push({ id: shippingId } as Shipping)
    }

    this.setProductProps({ shippings })
  }

  onProductsTableDidMount = () => {
    this.setSkuCountRange(initialFilterRange)
    this.setSearchProps(initialProductsSearchProps)
    this.setPaginationProps(initialProductTablePaginationProps)
    this.rootStore[PRODUCT_SKU_STORE].onProductsTableDidMount()
  }

  @computed get paginationInfo (): PaginationButtonsProps {
    return {
      elementsPerPage: this.pagination.size,
      selectedPageNumber: this.pagination.page,
      totalElements: this.pagination.totalElements,
      totalPages: this.pagination.totalPages
    }
  }

  @computed get createdDatePeriodFilter (): OnSelectCallbackParam {
    const { startDate, endDate } = this.searchProps
    if (!startDate || !endDate) return null

    return {
      start: moment(startDate),
      end: moment(endDate)
    }
  }

  @computed get productShippingIds (): Set<number> {
    if (
      !this.productToView ||
      !this.productToView.shippings ||
      !this.productToView.shippings.length
    ) {
      return new Set()
    }

    return new Set(this.productToView.shippings.map(shipping => shipping.id))
  }

  @computed get hasBrandShippings (): boolean {
    return !!this.brandShippings.length
  }

  @observable isColumnMarked = computedFn(
    (sortProp: ProductsTableSortProps): boolean => {
      return this.pagination.sortProp === sortProp
    }
  )

  @observable getSortDirection = computedFn(
    (sortProp: ProductsTableSortProps): SortDirection => {
      if (this.isColumnMarked(sortProp)) {
        return this.pagination.sortDirection
      }
      return SortDirection.ASC
    }
  )
}
